import { useGtm } from '@gtm-support/vue-gtm'
import { useDataAttributes } from './dataAttributes'
import { assertPrimitive } from '@/helpers/assert.helpers'
import { useMappingStore } from '@/stores/mappingStore'
import { useCalculationStore } from '@/stores/calculationStore'

export type UseTracking = ReturnType<typeof useTracking>

const partnerFallback = 'No partner ID available'

export const useTracking = () => {
  // @ts-ignore
  const gtm = useGtm()
  const { variant, partnerId } = useDataAttributes()
  const mappingStore = useMappingStore()
  const calculationStore = useCalculationStore()

  /**
   * Retrieves the current tracking values for the mini variant,
   * including monthly rate, purchase price, salary, and tax class.
   */
  const getMiniVariantTackingValues = () => {
    const serviceKey = 'calculator'
    const monthlyRate = calculationStore.calculatedResults.monthlyNetBurden

    const price = mappingStore.getFieldsOfKey(serviceKey, 'purchase_price').value[0]?.config.value ?? null
    const salary = mappingStore.getFieldsOfKey(serviceKey, 'monthly_salary').value[0]?.config.value ?? null
    const taxClass = mappingStore.getFieldsOfKey(serviceKey, 'tax_class').value[0]?.config.value ?? null

    return {
      monthlyRate,
      price,
      salary,
      taxClass
    }
  }

  /**
   * Tracks the consent update on partner site
   */
  const partnerConsentUpdate = (consent: boolean) => {
    if (!gtm.enabled()) return
    gtm.trackEvent({ event: 'partner_consent_update', consent })
  }

  /**
   * Tracks a view event for the mini variant.
   * Sends a `mini_lr_view` event to GTM with the partner hostname.
   */
  const viewMini = () => {
    if (!gtm.enabled() || variant !== 'mini') return
    gtm.trackEvent({
      event: 'mini_lr_view',
      mini_lr: {
        action: 'view',
        partner: partnerId ?? partnerFallback
      }
    })
  }

  /**
   * Tracks a value change interaction in the mini variant.
   * Sends a `mini_lr_interaction` event to GTM with the current calculator values.
   */
  const changeValueMini = () => {
    const { price, salary, taxClass, monthlyRate } = getMiniVariantTackingValues()

    if (!gtm.enabled() || variant !== 'mini') return
    if ([price, salary, taxClass, monthlyRate].some((value) => typeof value !== 'number')) return

    gtm.trackEvent({
      event: 'mini_lr_interaction',
      mini_lr: {
        action: 'interaction',
        price, // 2500
        salary, // 3000
        tax_class: taxClass, // 1
        monthly_rate: monthlyRate, // 41.56€
        partner: partnerId ?? partnerFallback
      }
    })
  }

  /**
   * Tracks a CTA click redirect in the mini variant.
   * Sends a `mini_lr_cta` event to GTM with the current calculator values.
   */
  const redirectMini = () => {
    const { price, salary, taxClass, monthlyRate } = getMiniVariantTackingValues()

    if (!gtm.enabled() || variant !== 'mini') return
    assertPrimitive(price, 'number')
    assertPrimitive(salary, 'number')
    assertPrimitive(taxClass, 'number')

    gtm.trackEvent({
      event: 'mini_lr_cta',
      mini_lr: {
        action: 'cta_click',
        price, // 2500
        salary, // 3000
        tax_class: taxClass, // 1
        monthly_rate: monthlyRate, // 41.56€
        partner: partnerId ?? partnerFallback
      }
    })
  }

  return { viewMini, changeValueMini, redirectMini, partnerConsentUpdate }
}
